import { useEffect, useState, type ReactNode } from 'react';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { useBusinessDay } from '../../../../shared/hooks/useBusinessDay.js';

interface ExecutiveSummary {
  readonly generatedAt: string;
  readonly kpis: { readonly active: number };
  readonly dashboards: { readonly published: number };
  readonly insights: {
    readonly openCritical: number;
    readonly openWarning: number;
    readonly openInfo: number;
    readonly openTotal: number;
  };
  readonly trends: { readonly predictedBreaches: number };
  readonly performance: { readonly recentSnapshots: number };
}

const TOTALS: readonly { key: string; labelKey: string; value: (s: ExecutiveSummary) => number }[] = [
  { key: 'active-kpis', labelKey: 'm37.executive.total.activeKpis', value: (s) => s.kpis.active },
  { key: 'published-dashboards', labelKey: 'm37.executive.total.publishedDashboards', value: (s) => s.dashboards.published },
  { key: 'open-critical', labelKey: 'm37.executive.total.openCritical', value: (s) => s.insights.openCritical },
  { key: 'open-total', labelKey: 'm37.executive.total.openInsights', value: (s) => s.insights.openTotal },
  { key: 'predicted-breaches', labelKey: 'm37.executive.total.predictedBreaches', value: (s) => s.trends.predictedBreaches },
  { key: 'recent-snapshots', labelKey: 'm37.executive.total.performanceSnapshots', value: (s) => s.performance.recentSnapshots },
];

/**
 * M37 — Executive Analytics Dashboard (§28). Read-only §5 executive roll-up
 * across the tenant's analytics estate: active KPI count, published dashboards,
 * open decision insights split by severity (critical / warning / info), the
 * number of predicted threshold breaches from active trend projections and the
 * recent performance-snapshot volume. Backed by the audited executive-summary
 * query (ANALYTICS_QUERIED). Tenant isolation is enforced server-side (LAW 4).
 */
export function ExecutiveAnalyticsDashboard(): ReactNode {
  const { token } = useAuth();
  const { t } = useTranslation();
  // Instants follow the tenant's DISPLAY_DATE_FORMAT, not the reader's language picker.
  const { at } = useBusinessDay();
  const [data, setData] = useState<ExecutiveSummary | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    apiRequest<{ data: ExecutiveSummary }>(`/v1/bi/analytics/executive-summary`, { token })
      .then((r) => { if (alive) { setData(r.data); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token]);

  return (
    <main aria-label="executive-analytics-dashboard">
      <h1>{t('m37.executive.title')}</h1>
      {error && <p role="alert">{error}</p>}
      {loading || !data ? <p>{t('common.loading')}</p> : (
        <>
          <section aria-label="executive-totals" className="kpi-grid">
            {TOTALS.map(({ key, labelKey, value }) => (
              <div key={key} className="kpi-card" data-kpi={key}>
                <span className="kpi-label">{t(labelKey)}</span>
                <span className="kpi-value" aria-label={`total-${key}`}>{value(data)}</span>
              </div>
            ))}
          </section>

          <dl aria-label="executive-insights">
            <dt>{t('m37.executive.openCritical')}</dt>
            <dd aria-label="insights-critical">{data.insights.openCritical}</dd>
            <dt>{t('m37.executive.openWarning')}</dt>
            <dd aria-label="insights-warning">{data.insights.openWarning}</dd>
            <dt>{t('m37.executive.openInfo')}</dt>
            <dd aria-label="insights-info">{data.insights.openInfo}</dd>
            <dt>{t('m37.executive.openTotal')}</dt>
            <dd aria-label="insights-total">{data.insights.openTotal}</dd>
            <dt>{t('m37.executive.predictedBreaches')}</dt>
            <dd aria-label="trends-breaches">{data.trends.predictedBreaches}</dd>
            <dt>{t('m37.executive.recentSnapshots')}</dt>
            <dd aria-label="performance-snapshots">{data.performance.recentSnapshots}</dd>
          </dl>

          <p aria-label="generated-at">{t('m37.executive.generated', { when: at(data.generatedAt) })}</p>
        </>
      )}
    </main>
  );
}
